import { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FaUser, FaSignOutAlt, FaBars, FaTimes } from 'react-icons/fa';

const Header = () => {
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleChatbotClick = () => {
    if (isAuthenticated) {
      navigate('/chatbot');
    } else {
      toast.error('Please sign in to use the chatbot feature', {
        position: 'top-right',
        autoClose: 5000,
      });
    }
  };

  const handleMenuChatbotClick = (e) => {
    e.preventDefault();
    handleChatbotClick();
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Cleanup the style sheet on component unmount
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes spinSlow {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      .animate-spin-slow {
        animation: spinSlow 4s linear infinite;
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Determine dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user?.role) return '/';
    switch (user.role.toLowerCase()) {
      case 'admin':
        return '/admin';
      case 'farmer':
        return '/farmer';
      case 'waste_collector':
        return '/waste-collector';
      default:
        return '/user';
    }
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5 flex items-center justify-between">
        <div className="flex-shrink-0 flex items-center space-x-2 sm:space-x-4">
          <img
            src="/logos/logo.png"
            alt="Chicken Farm Experts"
            className="h-12 sm:h-16 md:h-20 w-auto cursor-pointer"
            onClick={() => navigate('/')}
            style={{ objectFit: 'contain' }}
          />
          <span
            className="text-base sm:text-xl md:text-2xl font-bold text-primary cursor-pointer"
            onClick={() => navigate('/')}
          >
          </span>
        </div>
        <nav className="flex-1 hidden md:flex justify-center items-center space-x-2 sm:space-x-4 md:space-x-6">
          <Link to="/" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Home
          </Link>
          <Link to="/specialists" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Specialities
          </Link>
          <Link to="/WasteRequests" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Waste Requests
          </Link>
          <Link to="/services" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Services
          </Link>
          <Link to="/subscriptions" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Subscription
          </Link>
          <Link to="/farming-insights" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Farming Insights
          </Link>
          <Link to="/contact" className="text-gray-600 hover:text-secondary text-xs sm:text-sm md:text-base transition duration-200">
            Contact Us
          </Link>
          <Link
            to="/chatbot"
            onClick={handleMenuChatbotClick}
            className="bg-primary text-white px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full font-semibold text-xs sm:text-sm md:text-base hover:bg-secondary transition duration-200"
          >
            Chatbot
          </Link>
        </nav>
        <div className="flex-shrink-0">
          {user ? (
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-1 sm:space-x-2 p-1 sm:p-2 rounded-lg hover:bg-gray-100 transition duration-200"
              aria-label="User menu"
            >
              <FaUser className="text-gray-600" size={14} />
              <span className="text-xs sm:text-sm md:text-base font-medium text-gray-700">{user?.name || 'User'}</span>
            </button>
          ) : (
            <Link
              to="/login"
              className="bg-primary text-white px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-lg text-xs sm:text-sm md:text-base hover:bg-secondary transition duration-300 font-semibold"
            >
              Login
            </Link>
          )}
          {user && isDropdownOpen && (
            <div className="absolute right-0 mt-1 sm:mt-2 w-36 sm:w-40 md:w-48 bg-white rounded-md shadow-lg py-1 z-10">
              <Link
                to={getDashboardRoute()}
                className="block px-2 sm:px-3 md:px-4 py-1 sm:py-2 text-xs sm:text-sm md:text-base text-gray-700 hover:bg-gray-100 transition duration-200"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="w-full text-left px-2 sm:px-3 md:px-4 py-1 sm:py-2 text-xs sm:text-sm md:text-base text-gray-700 hover:bg-gray-100 transition duration-200 flex items-center"
              >
                <FaSignOutAlt className="mr-1 sm:mr-2" /> Logout
              </button>
            </div>
          )}
        </div>
        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden flex items-center p-1 sm:p-2 text-gray-600 hover:text-secondary"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
        </button>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-md p-2 sm:p-4">
          <Link to="/" className="block text-gray-600 hover:text-secondary py-1 sm:py-2 text-sm transition duration-200">
            Home
          </Link>
          <Link to="/specialists" className="block text-gray-600 hover:text-secondary py-1 sm:py-2 text-sm transition duration-200">
            Specialities
          </Link>
          <Link to="/services" className="block text-gray-600 hover:text-secondary py-1 sm:py-2 text-sm transition duration-200">
            Services
          </Link>
          <Link to="/subscriptions" className="block text-gray-600 hover:text-secondary py-1 sm:py-2 text-sm transition duration-200">
            Subscription
          </Link>
          <Link to="/contact" className="block text-gray-600 hover:text-secondary py-1 sm:py-2 text-sm transition duration-200">
            Contact Us
          </Link>
          <Link
            to="/chatbot"
            onClick={handleMenuChatbotClick}
            className="block bg-primary text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-semibold hover:bg-secondary transition duration-200 text-sm mt-2"
          >
            Chatbot
          </Link>
        </div>
      )}
      <button
        onClick={handleChatbotClick}
        className="fixed bottom-2 sm:bottom-4 md:bottom-6 right-2 sm:right-4 md:right-6 bg-primary text-white p-2 sm:p-3 md:p-4 rounded-full shadow-lg hover:bg-secondary transition-colors duration-300 z-50"
        aria-label="Open Chatbot"
      >
        <svg
          className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 animate-spin-slow"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-13h4v2h-4V7zm0 4h4v6h-4v-6z"
          />
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2" />
        </svg>
        <span className="sr-only">Chatbot icon by juicy_fish - Flaticon</span>
      </button>
    </header>
  );
};

export default Header;